// Small building blocks shared by every page.
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { words } from '../lib/format';

export function Audience({ item }: { item: { private: boolean; seen_by: string } }) {
  return (
    <span className={`badge ${item.private ? 'private' : 'shared'}`} title={item.private ? 'Private draft: nobody else can see it' : item.seen_by}>
      {item.private ? (
        <>
          <LockIcon /> Only you
        </>
      ) : (
        'Shared'
      )}
    </span>
  );
}

export function Demo({ show = true }: { show?: boolean }) {
  if (!show) return null;
  return (
    <span className="badge demo" title="Synthetic demo data (app/demo.py). Remove it with `app.cli purge-demo`.">
      Demo
    </span>
  );
}

const TONE: Record<string, string> = {
  proposed: 'neutral',
  ready: 'info',
  running: 'info',
  waiting_for_input: 'warn',
  waiting_for_approval: 'warn',
  completed: 'ok',
  failed: 'bad',
  cancelled: 'muted',
  approved: 'ok',
  rejected: 'muted',
  deferred: 'muted',
  needs_evidence: 'warn',
  superseded: 'muted',
  incubator: 'neutral',
  exploring: 'info',
  active: 'ok',
  paused: 'muted',
  done: 'ok',
  dropped: 'muted',
  queued: 'neutral',
  succeeded: 'ok',
  refused: 'bad',
  stored: 'neutral',
  ready_to_revisit: 'warn',
  converted: 'ok',
  archived: 'muted',
  planned: 'info',
  held: 'ok',
  decide: 'warn',
  review: 'neutral',
  discussed: 'ok',
};

export function Chip({ value }: { value: string }) {
  return <span className={`chip ${TONE[value] ?? 'neutral'}`}>{words(value)}</span>;
}

export function LockIcon() {
  return (
    <svg className="icon" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M4.5 7V5a3.5 3.5 0 1 1 7 0v2h.5a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1h.5Zm1.5 0h4V5a2 2 0 1 0-4 0v2Z" fill="currentColor" />
    </svg>
  );
}

export function Loading({ what = 'Loading' }: { what?: string }) {
  return (
    <div className="state loading" role="status" aria-live="polite">
      <span className="spinner" aria-hidden="true" /> {what}…
    </div>
  );
}

export function ErrorBox({ error, retry }: { error: string; retry?: () => void }) {
  return (
    <div className="state error" role="alert">
      <strong>Could not load this.</strong> {error}
      {retry && (
        <button type="button" className="btn small" onClick={retry}>
          Try again
        </button>
      )}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="state empty">{children}</div>;
}

export function NotYet({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="state notyet">
      <strong>{title}</strong>
      <div>{children}</div>
    </div>
  );
}

export function PageHead({ title, sub, actions }: { title: ReactNode; sub?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="page-head">
      <div>
        <h1>{title}</h1>
        {sub && <p className="muted">{sub}</p>}
      </div>
      {actions && <div className="row gap wrap">{actions}</div>}
    </header>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
      {hint && <small className="muted">{hint}</small>}
    </label>
  );
}

export function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  const ids = useId();
  const box = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const restore = document.activeElement as HTMLElement | null;
    const first = box.current?.querySelector<HTMLElement>('input, textarea, select, button');
    first?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close.current();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      restore?.focus();
    };
  }, []);
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={box} role="dialog" aria-modal="true" aria-labelledby={`${ids}-t`} className={`modal${wide ? ' wide' : ''}`}>
        <header className="modal-head">
          <h2 id={`${ids}-t`}>{title}</h2>
          <button type="button" className="btn ghost small" onClick={onClose} aria-label="Close">
            Close
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}

export function VisibilityPicker({
  value,
  onChange,
  workspaceName,
}: {
  value: 'workspace' | 'personal';
  onChange: (v: 'workspace' | 'personal') => void;
  workspaceName: string;
}) {
  return (
    <fieldset className="segmented" aria-label="Who can see it">
      <legend>Who can see it</legend>
      <label className={value === 'workspace' ? 'on' : ''}>
        <input type="radio" name="vis" checked={value === 'workspace'} onChange={() => onChange('workspace')} />
        Everyone in {workspaceName}
      </label>
      <label className={value === 'personal' ? 'on' : ''}>
        <input type="radio" name="vis" checked={value === 'personal'} onChange={() => onChange('personal')} />
        <LockIcon /> Only me (private draft)
      </label>
    </fieldset>
  );
}

export function Due({ iso }: { iso: string | null }) {
  if (!iso) return null;
  const d = new Date(`${iso}T23:59:59`);
  const days = Math.ceil((d.getTime() - Date.now()) / 86_400_000);
  const tone = days < 0 ? 'late' : days <= 3 ? 'soon' : 'later';
  const text = days < 0 ? `${-days}d late` : days === 0 ? 'today' : days <= 14 ? `in ${days}d` : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return (
    <span className={`due ${tone}`} title={`Due ${d.toLocaleDateString('en-GB')}`}>
      {text}
    </span>
  );
}
